import { useState, useCallback } from 'react';
import { BrokerWorkflow, parseWorkflowFlags, stringifyWorkflowFlags } from '../types/brokerworkflow/broker-workflow';
import { postData } from '../api/glAxios';
import { getBrokerWorkflowQuery, updateBrokerWorkflowQuery } from '../api/glQueries';

interface BrokerWorkflowFormValues {
    id: string;
    companyId: string;
    miqMaster: boolean;
    salesforceMaster: boolean;
    miq: { [key: string]: boolean };
    salesforce: { [key: string]: boolean };
}

interface UseBrokerWorkflowReturn {
    formValues: BrokerWorkflowFormValues | null;
    loading: boolean;
    error: string | null;
    isSaving: boolean;
    fetchBrokerWorkflow: (siteId: string) => Promise<void>;
    saveBrokerWorkflow: (formValues: BrokerWorkflowFormValues, regionId: string, siteId: string, languageCode: string) => Promise<boolean>;
    clearError: () => void;
}

export const useBrokerWorkflow = (): UseBrokerWorkflowReturn => {
    const [formValues, setFormValues] = useState<BrokerWorkflowFormValues | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const fetchBrokerWorkflow = useCallback(async (siteId: string) => {
        if (!siteId) {
            setError('SiteID is required');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const queryData = getBrokerWorkflowQuery(siteId);
            const response = await postData(queryData);

            if (typeof response.data === 'undefined' || response.errors) {
                if (typeof response === 'string') {
                    console.error('Failed to fetch broker workflow:', response);
                }
                const message = typeof response === 'string'
                    ? 'Failed to fetch broker workflow. Please try again.'
                    : (response.errors && response.errors[0] && response.errors[0].message) || 'Failed to fetch broker workflow';
                setError(message);
                setFormValues(null);
                return;
            }

            const brokerWorkflow = response.data && response.data.brokerWorkflowBySiteId;

            if (!brokerWorkflow) {
                // No existing workflow, create empty form
                setFormValues({
                    id: '',
                    companyId: '',
                    miqMaster: false,
                    salesforceMaster: false,
                    miq: {},
                    salesforce: {}
                });
                return;
            }

            // Parse JSON strings to objects
            const miqFlags = parseWorkflowFlags(brokerWorkflow.mIQ);
            const salesforceFlags = parseWorkflowFlags(brokerWorkflow.salesforce);

            setFormValues({
                id: brokerWorkflow.id,
                companyId: brokerWorkflow.companyID != null ? brokerWorkflow.companyID.toString() : '',
                miqMaster: brokerWorkflow.enable_WorkFlow_MIQ,
                salesforceMaster: brokerWorkflow.enable_WorkFlow_Salesforce,
                miq: miqFlags,
                salesforce: salesforceFlags
            });
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'An error occurred';
            setError(errorMessage);
            setFormValues(null);
        } finally {
            setLoading(false);
        }
    }, []);

    const saveBrokerWorkflow = useCallback(
        async (
            values: BrokerWorkflowFormValues,
            regionId: string,
            siteId: string,
            languageCode: string
        ) => {
            setIsSaving(true);
            setError(null);

            try {
                // Convert form values back to BrokerWorkflow type
                // Note: updatedBy and updatedAt are NOT included as they are server-managed
                const workflowData: BrokerWorkflow = {
                    id: values.id, // Send the ID (empty if new, UUID if existing)
                    regionID: regionId,
                    siteID: siteId,
                    mIQ: stringifyWorkflowFlags(values.miq),
                    salesforce: stringifyWorkflowFlags(values.salesforce),
                    enable_WorkFlow_MIQ: values.miqMaster,
                    enable_WorkFlow_Salesforce: values.salesforceMaster,
                    updatedBy: null,
                    updatedAt: new Date().toISOString(),
                    languageCode: languageCode,
                    companyID: values.companyId ? parseInt(values.companyId, 10) : null
                };

                // Remove server-managed fields before sending to GraphQL
                const { updatedBy, updatedAt, ...inputData } = workflowData;

                const mutationData = updateBrokerWorkflowQuery(inputData as BrokerWorkflow);
                const response = await postData(mutationData);

                if (typeof response.data === 'undefined' || response.errors) {
                    if (typeof response === 'string') {
                        console.error('Failed to save broker workflow:', response);
                    }
                    const message = typeof response === 'string'
                        ? 'Failed to save broker workflow. Please try again.'
                        : (response.errors && response.errors[0] && response.errors[0].message) || 'Failed to save broker workflow';
                    setError(message);
                    return false;
                }

                // Update form values with server response
                const updatedWorkflow = response.data && response.data.updateBrokerWorkflow;
                if (updatedWorkflow) {
                    const miqFlags = parseWorkflowFlags(updatedWorkflow.mIQ);
                    const salesforceFlags = parseWorkflowFlags(updatedWorkflow.salesforce);

                    setFormValues({
                        id: updatedWorkflow.id,
                        companyId: updatedWorkflow.companyID != null ? updatedWorkflow.companyID.toString() : '',
                        miqMaster: updatedWorkflow.enable_WorkFlow_MIQ,
                        salesforceMaster: updatedWorkflow.enable_WorkFlow_Salesforce,
                        miq: miqFlags,
                        salesforce: salesforceFlags
                    });
                }
                return true;
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'An error occurred';
                setError(errorMessage);
                return false;
            } finally {
                setIsSaving(false);
            }
        },
        []
    );

    return {
        formValues,
        loading,
        error,
        isSaving,
        fetchBrokerWorkflow,
        saveBrokerWorkflow,
        clearError
    };
};
